'use client';
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { ClimbEntry } from '@/lib/climbData';

interface TimeDistributionChartProps {
  entries: ClimbEntry[];
  climbName: string;
}

function buildBuckets(entries: ClimbEntry[]) {
  const approved = entries.filter((e) => e.status === 'approved');
  if (approved.length === 0) return [];
  const minT = Math.min(...approved.map((e) => e.timeSeconds));
  const maxT = Math.max(...approved.map((e) => e.timeSeconds));
  const range = maxT - minT || 600;
  const bucketSize = Math.ceil(range / 6);
  const buckets: { label: string; count: number; rangeStart: number }[] = [];
  for (let i = 0; i < 6; i++) {
    const start = minT + i * bucketSize;
    const end = start + bucketSize;
    const count = approved.filter((e) => e.timeSeconds >= start && e.timeSeconds < end).length;
    const mins = Math.floor(start / 60);
    const secs = start % 60;
    buckets.push({
      label: `${mins}:${secs.toString().padStart(2, '0')}`,
      count,
      rangeStart: start,
    });
  }
  return buckets;
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-xl">
        <p className="text-xs text-muted-foreground mb-1">Da {label}</p>
        <p className="text-sm font-bold text-foreground font-mono-nums">{payload[0].value} ciclisti</p>
      </div>
    );
  }
  return null;
};

export default function TimeDistributionChart({ entries, climbName }: TimeDistributionChartProps) {
  const data = buildBuckets(entries);
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="bg-card border border-border rounded-xl p-5 card-glow">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Distribuzione Tempi</h3>
          <p className="text-xs text-muted-foreground">{climbName}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="w-3 h-3 rounded-sm bg-primary inline-block" />
          Ciclisti per fascia
        </div>
      </div>
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-dist-${index}`}
                  fill={entry.count === maxCount ? 'var(--accent)' : 'var(--primary)'}
                  fillOpacity={entry.count === maxCount ? 1 : 0.7}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">
          Nessun dato disponibile
        </div>
      )}
    </div>
  );
}